import { differenceInCalendarDays } from 'date-fns';
import { parseDateOnly, toDateKey } from '@/lib/date';
import { TASKS_PAGE_LIMIT } from '@/hooks/use-tasks';

/**
 * What the dashboard's "Due soon" list shows, and out of what.
 *
 * This list has no server-side filter to lean on: the tasks endpoint can order
 * by due date but cannot exclude finished work, and a board's finished issues
 * outnumber its open ones several times over — so asking for the soonest-due
 * page would return years-old closed work. The list therefore scans a page of
 * each board and filters here, which makes the SIZE of that page part of the
 * answer rather than an implementation detail.
 */

/** How many rows the dashboard shows. */
export const DUE_SOON_MAX_ROWS = 6;

/** Anything due within this many days counts as "soon"; overdue always counts. */
export const DUE_SOON_HORIZON_DAYS = 7;

/**
 * How much of each board to read.
 *
 * The server's own maximum page, which is the largest pool a single request can
 * produce. Halving it to 50 was measured against the demo database: the
 * candidate pool fell 31 → 13 on Payments Gateway Migration and 24 → 8 on
 * Loyalty Programme Revamp, and two issues overdue since 24 July dropped off
 * the dashboard, replaced by work due later. Nothing on screen said so, because
 * a list that silently scans less looks exactly like a shorter list.
 */
export const DUE_SOON_SCAN_LIMIT = TASKS_PAGE_LIMIT;

export interface DueSoonTask {
  id: string;
  key: string;
  title: string;
  dueDate: string | null;
  status: { category: string };
}

export interface DueSoonBoard {
  id: string;
  slug: string;
  name: string;
}

/** One board's loaded page. */
export interface DueSoonInput<
  TTask extends DueSoonTask = DueSoonTask,
  TBoard extends DueSoonBoard = DueSoonBoard,
> {
  board: TBoard;
  tasks: readonly TTask[];
}

export interface DueSoonRow<
  TTask extends DueSoonTask = DueSoonTask,
  TBoard extends DueSoonBoard = DueSoonBoard,
> {
  task: TTask;
  board: TBoard;
  /** Index of the board in the input, for the colour the dashboard assigns by position. */
  boardIndex: number;
}

/** Work that is finished or abandoned is not due — it is done with. */
const CLOSED = new Set(['done', 'cancelled']);

/**
 * The soonest-due open work across every board handed in, oldest first.
 *
 * Overdue issues sort to the top because they are the most negative, which is
 * the whole point of the list: the thing that needed doing last week should not
 * be below the thing due on Friday.
 */
export function selectDueSoon<TTask extends DueSoonTask, TBoard extends DueSoonBoard>(
  boards: readonly DueSoonInput<TTask, TBoard>[],
  options?: { today?: string; max?: number },
): DueSoonRow<TTask, TBoard>[] {
  const today = parseDateOnly(options?.today ?? toDateKey(new Date()));
  const max = options?.max ?? DUE_SOON_MAX_ROWS;

  const rows: (DueSoonRow<TTask, TBoard> & { days: number })[] = [];

  boards.forEach(({ board, tasks }, boardIndex) => {
    for (const task of tasks) {
      if (!task.dueDate) continue;
      if (CLOSED.has(task.status?.category)) continue;

      const due = parseDateOnly(task.dueDate);
      if (Number.isNaN(due.getTime())) continue;

      const days = differenceInCalendarDays(due, today);
      if (days > DUE_SOON_HORIZON_DAYS) continue;

      rows.push({ task, board, boardIndex, days });
    }
  });

  return rows
    .sort((a, b) => a.days - b.days || a.task.key.localeCompare(b.task.key))
    .slice(0, max)
    .map(({ task, board, boardIndex }) => ({ task, board, boardIndex }));
}
